import { useEffect, useMemo, useState } from 'react';

import { Button, Dropdown, FieldLabel, Modal, Multiselect, NumericInput, showSnackbar, TextArea, TextInput } from 'fluxo-ui';
import { ArchivedIcon, CheckCircleIcon, EyeIcon, PlayIcon, SaveIcon, TimesIcon } from 'fluxo-ui/icons';

import { QUESTION_TYPES } from '../../common/constants';
import { useCategoryStore } from '../../store';
import { Badge, Card, TimePicker } from '../common';

import MarkdownEditor from './MarkdownEditor';
import MediaUploader from './MediaUploader';
import OptionEditor from './OptionEditor';
import QuestionPreview from './QuestionPreview';
import QuestionTypeSelector from './QuestionTypeSelector';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface QuestionData {
    _id?: string;
    type: string;
    title: string;
    text: string;
    category?: string[];
    tags?: string[];
    difficulty?: string;
    visibility?: string;
    status?: string;
    archivedStatus?: string;
    defaultMarks?: number;
    defaultTimeSeconds?: number;
    versionNumber?: number;
    isLatestVersion?: boolean;
    parentQuestionId?: string;
    publishedAt?: string;
    options?: Array<{
        id: string;
        text: string;
        isCorrect: boolean;
        partialCredit?: number;
        feedback?: string;
        description?: string;
    }>;
    correctAnswer?: string | string[];
    explanation?: string;
    hints?: string[];
    media?: any[];
    resourceAttachments?: any[];
    aiValidationResult?: {
        isValid: boolean;
        issues: string[];
        suggestions: string[];
        correctedData?: any;
        validatedAt: string;
    };
}

interface QuestionEditorProps {
    question?: QuestionData;
    onSave?: (question: QuestionData, action: 'draft' | 'publish') => Promise<void>;
    onCancel?: () => void;
    onArchive?: (question: QuestionData) => Promise<QuestionData | void>;
    onValidateAI?: (question: QuestionData) => Promise<any>;
    loading?: boolean;
}

// Must match the backend difficulty enum: beginner, easy, intermediate, hard, expert
const difficulties: ListItem[] = [
    { label: 'Beginner', value: 'beginner' },
    { label: 'Easy', value: 'easy' },
    { label: 'Intermediate', value: 'intermediate' },
    { label: 'Hard', value: 'hard' },
    { label: 'Expert', value: 'expert' },
];

const visibilityOptions: ListItem[] = [
    { label: 'Private', value: 'private' },
    { label: 'Organization', value: 'organization' },
    { label: 'Public', value: 'public' },
];

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onSave, onCancel, onArchive, onValidateAI, loading = false }) => {
    const { categories: categoryList, fetchCategories } = useCategoryStore();

    const [formData, setFormData] = useState<QuestionData>(
        question || {
            type: QUESTION_TYPES.MCSA,
            title: '',
            text: '',
            status: 'draft',
            archivedStatus: 'active',
            defaultMarks: 1,
            defaultTimeSeconds: 60,
            versionNumber: 1,
            isLatestVersion: true,
        },
    );
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showAIValidation, setShowAIValidation] = useState(false);

    useEffect(() => {
        if (question) {
            setFormData(question);
        }
    }, [question]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const categories: ListItem[] = useMemo(() => {
        return categoryList
            .filter((cat) => cat.status === 'active' || cat.status === 'Active')
            .map((cat) => ({
                label: cat.name,
                value: cat._id,
            }));
    }, [categoryList]);

    const updateField = <K extends keyof QuestionData>(field: K, value: QuestionData[K]) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title?.trim()) {
            newErrors.title = 'Question title is required';
        }

        if (!formData.text?.trim()) {
            newErrors.text = 'Question text is required';
        }

        if (!formData.category || formData.category.length === 0) {
            newErrors.category = 'At least one category is required';
        }

        const isMCQ = formData.type === QUESTION_TYPES.MCSA || formData.type === QUESTION_TYPES.MCMA;
        if (isMCQ && (!formData.options || formData.options.length < 2)) {
            newErrors.options = 'At least 2 options are required';
        }

        if (isMCQ && !formData.options?.some((opt) => opt.isCorrect)) {
            newErrors.options = 'At least one correct answer must be selected';
        }

        if (formData.type === QUESTION_TYPES.FILL_BLANKS && !formData.correctAnswer) {
            newErrors.correctAnswer = 'Correct answer is required for fill in the blanks questions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (action: 'draft' | 'publish') => {
        if (action === 'publish' && !validate()) {
            showSnackbar('Please fix the errors before publishing', 'Validation Error', { type: 'error' });
            return;
        }

        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                status: action === 'publish' ? 'approved' : 'draft',
                publishedAt: action === 'publish' && !formData.publishedAt ? new Date().toISOString() : formData.publishedAt,
            };
            await onSave?.(dataToSave, action);
            showSnackbar(`Question ${action === 'publish' ? 'published' : 'saved as draft'} successfully`, 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to save question', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async () => {
        if (!window.confirm('Are you sure you want to archive this question? Archived questions cannot be added to new exams.')) {
            return;
        }

        try {
            if (formData._id) {
                const updatedQuestion = await onArchive?.(formData);
                if (updatedQuestion) {
                    setFormData(updatedQuestion);
                }
                showSnackbar('Question archived successfully', 'Success', { type: 'success' });
            }
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to archive question', 'Error', { type: 'error' });
        }
    };

    const handleValidateAI = async () => {
        if (!validate()) {
            showSnackbar('Please fix basic errors before AI validation', 'Validation Error', { type: 'warning' });
            return;
        }

        setValidating(true);
        try {
            const result = await onValidateAI?.(formData);
            setFormData((prev) => ({ ...prev, aiValidationResult: result }));
            setShowAIValidation(true);
            if (result.isValid) {
                showSnackbar('Question validation passed!', 'Success', { type: 'success' });
            } else {
                showSnackbar(`Validation found ${result.issues.length} issue(s)`, 'Validation Complete', { type: 'warning' });
            }
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to validate question', 'Error', { type: 'error' });
        } finally {
            setValidating(false);
        }
    };

    const applyAICorrections = () => {
        if (formData.aiValidationResult?.correctedData) {
            setFormData((prev) => ({ ...prev, ...formData.aiValidationResult!.correctedData }));
            setShowAIValidation(false);
            showSnackbar('AI corrections applied', 'Success', { type: 'success' });
        }
    };

    const isMCQ =
        formData.type === QUESTION_TYPES.MCSA || formData.type === QUESTION_TYPES.MCMA || formData.type === QUESTION_TYPES.TRUE_FALSE;
    const isFillBlanks = formData.type === QUESTION_TYPES.FILL_BLANKS;
    const isPublished = formData.status === 'approved' && !!formData.publishedAt;
    const isArchived = formData.archivedStatus === 'archived';

    return (
        <div className="space-y-6">
            {/* Sticky action bar */}
            <div className="sticky top-14 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur border-b border-slate-200 dark:border-slate-800">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                            {question ? 'Edit Question' : 'Create Question'}
                        </h2>
                        {isPublished && <Badge tone="success" dot>{`Published v${formData.versionNumber}`}</Badge>}
                        {formData.status === 'draft' && <Badge tone="neutral" dot>Draft</Badge>}
                        {isArchived && <Badge tone="warning" dot>Archived</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            label="Preview"
                            leftIcon={EyeIcon}
                            variant="default"
                            layout="outlined"
                            size="sm"
                            onClick={() => setShowPreview(!showPreview)}
                        />
                        {onValidateAI && (
                            <Button
                                label="AI Validate"
                                leftIcon={PlayIcon}
                                variant="default"
                                layout="outlined"
                                size="sm"
                                onClick={handleValidateAI}
                                isLoading={validating}
                                disabled={validating || saving}
                            />
                        )}
                        {question && !isArchived && onArchive && (
                            <Button
                                label="Archive"
                                leftIcon={ArchivedIcon}
                                variant="danger"
                                layout="outlined"
                                size="sm"
                                onClick={handleArchive}
                                disabled={saving}
                            />
                        )}
                        {onCancel && (
                            <Button
                                label="Cancel"
                                leftIcon={TimesIcon}
                                variant="default"
                                layout="outlined"
                                size="sm"
                                onClick={onCancel}
                                disabled={saving}
                            />
                        )}
                        <span className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700" aria-hidden />
                        <Button
                            label="Save Draft"
                            leftIcon={SaveIcon}
                            variant="default"
                            size="sm"
                            onClick={() => handleSave('draft')}
                            isLoading={saving}
                            disabled={saving || loading || isArchived}
                        />
                        <Button
                            label="Publish"
                            leftIcon={CheckCircleIcon}
                            variant="primary"
                            size="sm"
                            onClick={() => handleSave('publish')}
                            isLoading={saving}
                            disabled={saving || loading || isArchived}
                        />
                    </div>
                </div>
                {isPublished && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                        This question is published — any edits will create a new version.
                    </p>
                )}
            </div>

            {/* AI Validation Results Modal */}
            {showAIValidation && formData.aiValidationResult && (
                <Modal isOpen={showAIValidation} onClose={() => setShowAIValidation(false)} title="AI Validation Results" size="lg">
                    <div className="space-y-4">
                        {formData.aiValidationResult.isValid ? (
                            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                <CheckCircleIcon className="w-5 h-5" />
                                <span className="font-semibold">Question validated successfully! No issues found.</span>
                            </div>
                        ) : (
                            <>
                                {formData.aiValidationResult.issues.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-red-600 dark:text-red-400 mb-2">Issues Found:</h3>
                                        <ul className="list-disc list-inside space-y-1 text-sm">
                                            {formData.aiValidationResult.issues.map((issue, idx) => (
                                                <li key={idx}>{issue}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {formData.aiValidationResult.suggestions.length > 0 && (
                                    <div>
                                        <h3 className="font-semibold text-teal-600 dark:text-teal-400 mb-2">Suggestions:</h3>
                                        <ul className="list-disc list-inside space-y-1 text-sm">
                                            {formData.aiValidationResult.suggestions.map((suggestion, idx) => (
                                                <li key={idx}>{suggestion}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {formData.aiValidationResult.correctedData && (
                                    <div className="flex items-center justify-between p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                                        <p className="text-sm text-teal-800 dark:text-teal-200">AI has prepared corrected data for you.</p>
                                        <Button label="Apply Corrections" variant="primary" size="sm" onClick={applyAICorrections} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {showPreview ? (
                <QuestionPreview question={formData} showAnswer={true} onClose={() => setShowPreview(false)} />
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
                    {/* Main authoring column */}
                    <div className="xl:col-span-2 space-y-6 min-w-0">
                        <Card title="Question Type" description="Determines how the question is answered and graded">
                            <QuestionTypeSelector
                                value={formData.type}
                                onChange={(type) => updateField('type', type)}
                                disabled={!!question || isPublished}
                            />
                        </Card>

                        <Card title="Content" description="What the participant sees when attempting this question">
                            <div className="space-y-4">
                                <FieldLabel label="Question Title" required error={errors.title}>
                                    <TextInput
                                        value={formData.title}
                                        onChange={(e: ComponentEvent<string>) => updateField('title', e.value)}
                                        placeholder="Enter a descriptive title for this question"
                                        disabled={loading}
                                        error={!!errors.title}
                                    />
                                </FieldLabel>

                                <FieldLabel label="Question Text" required error={errors.text}>
                                    <MarkdownEditor
                                        value={formData.text}
                                        onChange={(value) => updateField('text', value)}
                                        placeholder="Enter your question text here... Use {image:id}, {video:id}, {document:id} to embed media."
                                        disabled={loading}
                                    />
                                </FieldLabel>
                            </div>
                        </Card>

                        <Card title="Answer" description="Define the correct answer and grading behavior">
                            <div className="space-y-4">
                                {isMCQ && (
                                    <>
                                        {errors.options && (
                                            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-200">
                                                {errors.options}
                                            </div>
                                        )}
                                        <OptionEditor
                                            options={formData.options}
                                            onChange={(options) => updateField('options', options)}
                                            questionType={formData.type as any}
                                            disabled={loading}
                                            allowPartialCredit={formData.type === QUESTION_TYPES.MCMA}
                                        />
                                    </>
                                )}

                                {isFillBlanks && (
                                    <FieldLabel
                                        label="Correct Answer(s)"
                                        required
                                        error={errors.correctAnswer}
                                        hint="For multiple blanks, separate answers with commas or use an array"
                                    >
                                        <TextArea
                                            value={
                                                Array.isArray(formData.correctAnswer)
                                                    ? formData.correctAnswer.join(', ')
                                                    : formData.correctAnswer || ''
                                            }
                                            onChange={(e: ComponentEvent<string>) => {
                                                const answers = e.value.split(',').map((a) => a.trim());
                                                updateField('correctAnswer', answers.length === 1 ? answers[0] : answers);
                                            }}
                                            placeholder="Enter correct answer(s)"
                                            disabled={loading}
                                            rows={3}
                                        />
                                    </FieldLabel>
                                )}

                                {!isMCQ && !isFillBlanks && (
                                    <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                                        <p className="text-sm text-teal-800 dark:text-teal-200">
                                            This question type will be manually graded or auto-graded based on configured rules.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card title="Explanation & Hints" description="Shown to participants after submission or on request">
                            <div className="space-y-4">
                                <FieldLabel label="Explanation" hint="Explain the correct answer (shown after submission)">
                                    <MarkdownEditor
                                        value={formData.explanation || ''}
                                        onChange={(value) => updateField('explanation', value)}
                                        placeholder="Explain why the answer is correct..."
                                        rows={5}
                                        disabled={loading}
                                        showHelp={false}
                                    />
                                </FieldLabel>

                                <FieldLabel label="Hints" hint="Optional hints to help students">
                                    <Multiselect
                                        options={[]}
                                        value={formData.hints || []}
                                        onChange={(e: ComponentEvent<string[]>) => updateField('hints', e.value)}
                                        placeholder="Type hints and press Enter"
                                        disabled={loading}
                                    />
                                </FieldLabel>
                            </div>
                        </Card>

                        <Card
                            title="Media & Resources"
                            description="Embed media in the question text using {image:id}, {video:id}, {document:id}"
                        >
                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">Media Files</h4>
                                    <MediaUploader
                                        files={formData.media}
                                        onChange={(files) => updateField('media', files)}
                                        questionId={formData._id}
                                        disabled={loading}
                                    />
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Resource Attachments</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                                        Additional resources displayed to students when they attempt the question.
                                    </p>
                                    <MediaUploader
                                        files={formData.resourceAttachments}
                                        onChange={(files) => updateField('resourceAttachments', files)}
                                        questionId={formData._id}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Metadata sidebar */}
                    <div className="space-y-6 xl:sticky xl:top-32">
                        <Card title="Organize">
                            <div className="space-y-4">
                                <FieldLabel label="Category" required error={errors.category}>
                                    <Multiselect
                                        options={categories}
                                        value={formData.category || []}
                                        onChange={(e: ComponentEvent<string[]>) => updateField('category', e.value)}
                                        placeholder="Select categories"
                                        searchable
                                        disabled={loading}
                                    />
                                </FieldLabel>

                                <FieldLabel label="Difficulty">
                                    <Dropdown
                                        options={difficulties}
                                        value={formData.difficulty}
                                        onChange={(e: ComponentEvent<string>) => updateField('difficulty', e.value)}
                                        placeholder="Select difficulty"
                                        disabled={loading}
                                    />
                                </FieldLabel>

                                <FieldLabel label="Tags" hint="Add keywords for easier searching">
                                    <Multiselect
                                        options={[]}
                                        value={formData.tags || []}
                                        onChange={(e: ComponentEvent<string[]>) => updateField('tags', e.value)}
                                        placeholder="Type and press Enter to add tags"
                                        disabled={loading}
                                    />
                                </FieldLabel>

                                <FieldLabel label="Visibility">
                                    <Dropdown
                                        options={visibilityOptions}
                                        value={formData.visibility || 'private'}
                                        onChange={(e: ComponentEvent<string>) => updateField('visibility', e.value)}
                                        disabled={loading}
                                    />
                                </FieldLabel>
                            </div>
                        </Card>

                        <Card title="Scoring">
                            <div className="space-y-4">
                                <FieldLabel label="Default Marks" hint="0-100, can be overridden in exam">
                                    <NumericInput
                                        value={formData.defaultMarks}
                                        onChange={(e: ComponentEvent<number | undefined>) => updateField('defaultMarks', e.value)}
                                        min={0}
                                        max={100}
                                        disabled={loading}
                                    />
                                </FieldLabel>

                                <TimePicker
                                    label="Default Time"
                                    hint="Time to answer this question (can be overridden in exam)"
                                    value={formData.defaultTimeSeconds}
                                    onChange={(seconds) => updateField('defaultTimeSeconds', seconds)}
                                    disabled={loading}
                                    maxMinutes={60}
                                />
                            </div>
                        </Card>

                        {isPublished && (
                            <Card title="Version">
                                <dl className="space-y-2 text-sm">
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-slate-500 dark:text-slate-400">Current Version</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white tabular-nums">
                                            v{formData.versionNumber}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-slate-500 dark:text-slate-400">Published</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white">
                                            {formData.publishedAt ? new Date(formData.publishedAt).toLocaleString() : 'N/A'}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-slate-500 dark:text-slate-400">Latest Version</dt>
                                        <dd className="font-medium text-slate-900 dark:text-white">
                                            {formData.isLatestVersion ? 'Yes' : 'No'}
                                        </dd>
                                    </div>
                                </dl>
                                <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                                    Editing a published question will create a new version.
                                </p>
                            </Card>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionEditor;
